/**
 * The tool palette and its readout, positioned over the map.
 *
 * Mounted by the page as a sibling of <WeatherMap>, so WeatherMap costs nothing for
 * the pages that never show tools. The toggle that opens it lives in the page header,
 * and this floats over the map, so neither costs the map any height.
 */

import { formatDistance, formatEtaRange, type Asset, type EtaResult } from '@ww/shared';
import type { UseMapDraw } from '../hooks/useMapDraw';
import { humanEvent } from '../lib/text';
import type { DrawnZone, ToolMode } from '../lib/mapTools';

const TOOLS: Array<[ToolMode, string, string]> = [
  ['storm-path', 'Storm path', 'Tap a warning, or an area of rain, to project where it could go'],
  ['eta', 'Arrival', 'Tap a spot to see when weather reaches it, then drag the cone to aim a track by hand'],
  ['measure', 'Measure', 'Distance between two points, and crossing time'],
  ['zone-circle', 'Radius', 'Tap a centre, then set the radius'],
  ['zone-polygon', 'Area', 'Trace an area to watch'],
  ['markup', 'Draw', 'Freehand markup for a screenshot'],
];

export interface ImpactedPlace {
  key: string;
  name: string;
  kind: 'asset' | 'town';
  eta: EtaResult;
}

interface Props {
  /** The toggle lives in the page header; this renders the floating palette only. */
  onClose: () => void;
  draw: UseMapDraw;
  /** Sites and towns the projected path crosses, soonest first. */
  impacted?: ImpactedPlace[];
  swathMinutes?: number;
  onSwathMinutes?: (m: number) => void;
  /** Promote a drawn circle to a real, server-monitored Asset. */
  onSaveZone?: (zone: DrawnZone) => void;
  savingZoneId?: string | null;
}

export default function MapTools({ onClose, draw, impacted, swathMinutes, onSwathMinutes, onSaveZone, savingZoneId }: Props) {
  const { state, setMode, undo, clear, removeZone, commitPolygon, setManualBearing, pinEta, pinEtaManual, pickedPath, measureText, zoneEtas, subject } = draw;
  const hasContent = !!state.pin || state.measure.length > 0 || state.zones.length > 0 || state.strokes.length > 0;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', marginTop: 10 }}
    >
      {(pinEta || measureText || state.zones.length > 0) && (
        <div
          className="panel"
          style={{ padding: '9px 11px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}
          role="status"
        >
          {pinEta &&
            // With nothing on radar and no warning there is no weather to measure to,
            // so `distanceMiles` is 0 by default rather than by observation. Printing it
            // read as "weather is under a mile away", which is the opposite of the truth.
            (pinEta.reason === 'no-geometry' ? (
              <div>
                <strong style={{ color: '#eaf1fb' }}>Nothing headed for this spot</strong>
                <div style={{ color: '#93a5c1', fontSize: 12.5 }}>
                  Nothing on radar and no warnings near here.
                </div>
              </div>
            ) : (
              <div>
                {pinEtaManual && (
                  <div style={{ color: '#93a5c1', fontSize: 12, fontWeight: 600 }}>Observed</div>
                )}
                <strong style={{ color: '#eaf1fb' }}>
                  {pinEta.status === 'inside'
                    ? 'Weather is over this spot now'
                    : pinEta.status === 'arriving'
                      ? `Reaches this spot ${formatEtaRange(pinEta)}`
                      : pinEta.status === 'moving-away'
                        ? 'Not headed for this spot'
                        : 'No timing available for this spot'}
                </strong>
                <div style={{ color: '#93a5c1', fontSize: 12.5 }}>
                  {formatDistance(pinEta.distanceMiles)} away
                  {subject ? ` · ${humanEvent(subject.event).toLowerCase()}` : ''}
                </div>
                {pinEta.status === 'unknown' && pinEta.reason === 'no-motion' && (
                  <div style={{ color: '#93a5c1', fontSize: 12 }}>
                    We can see it, but nothing tells us which way it is moving.
                  </div>
                )}
                {pinEta.mayMiss && !pinEtaManual && (
                  <div style={{ color: '#ffc98a', fontSize: 12 }}>A small change of track would take it past.</div>
                )}
                {pinEtaManual && (
                  /*
                   * Both, side by side, never one replacing the other: a hand-aimed track
                   * is a question the user asked, not something the weather is doing, and
                   * the two must not be confusable.
                   */
                  <div
                    style={{
                      marginTop: 6,
                      paddingTop: 6,
                      borderTop: '1px solid #2a3b58',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    <div style={{ color: '#cbd5e8', fontSize: 12, fontWeight: 600 }}>
                      Your track — not the observed motion
                    </div>
                    <div style={{ color: '#c9d7ee' }}>
                      {pinEtaManual.status === 'inside'
                        ? 'Weather would already be here'
                        : pinEtaManual.status === 'arriving'
                          ? `Would reach here ${formatEtaRange(pinEtaManual)}`
                          : 'Would not reach this spot'}
                    </div>
                    <button
                      type="button"
                      className="chip hov"
                      style={{ alignSelf: 'flex-start', minHeight: 28 }}
                      onClick={() => setManualBearing(null)}
                    >
                      Reset to observed
                    </button>
                  </div>
                )}
              </div>
            ))}
          {measureText && <div style={{ color: '#c9d7ee' }}>{measureText}</div>}
          {state.zones.map((z) => {
            const eta = zoneEtas.get(z.id);
            const label =
              z.kind === 'circle' ? `${z.radiusMiles?.toFixed(1)} mi radius` : `${z.points.length}-corner area`;
            return (
              <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ color: '#c9d7ee' }}>{label}</span>
                <span style={{ color: eta?.status === 'arriving' || eta?.status === 'inside' ? '#ffa857' : '#93a5c1' }}>
                  {eta?.status === 'inside'
                    ? 'weather here now'
                    : eta?.status === 'arriving'
                      ? `weather enters ${formatEtaRange(eta)}`
                      : 'clear'}
                </span>
                {z.kind === 'circle' && onSaveZone && (
                  <button
                    type="button"
                    className="chip hov"
                    disabled={savingZoneId === z.id}
                    onClick={() => onSaveZone(z)}
                    title="Create a monitored site here, so the server watches it and can send SMS"
                  >
                    {savingZoneId === z.id ? 'Saving…' : 'Save as site'}
                  </button>
                )}
                <button type="button" className="chip hov" onClick={() => removeZone(z.id)} aria-label={`Remove ${label}`}>
                  ✕
                </button>
              </div>
            );
          })}
          {subject && (
            <div style={{ fontSize: 11.5, color: '#7d8ea8' }}>
              Estimates only — storms change speed and direction.
            </div>
          )}
        </div>
      )}

      {pickedPath && (
        <div className="panel" style={{ padding: '9px 11px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {pickedPath.refusal ? (
            <div style={{ color: '#ffc98a' }}>{pickedPath.refusal}</div>
          ) : (
            <>
              <strong style={{ color: '#eaf1fb' }}>
                Where {humanEvent(pickedPath.label).toLowerCase()} could be in the next {swathMinutes ?? 60} minutes
              </strong>
              <div style={{ color: '#93a5c1', fontSize: 12 }}>
                If it holds its current speed and direction.{' '}
                {pickedPath.motion?.source === 'radar'
                  ? 'Track estimated from radar.'
                  : 'Track from the National Weather Service warning.'}
              </div>
            </>
          )}
        </div>
      )}

      {!!impacted?.length && (
        <div className="panel" style={{ padding: '9px 11px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span className="lbl">In the projected path</span>
            {onSwathMinutes && (
              <div className="radar-seg" role="group" aria-label="Projection lead time">
                {[30, 60, 90].map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={swathMinutes === m}
                    onClick={() => onSwathMinutes(m)}
                  >
                    {m}m
                  </button>
                ))}
              </div>
            )}
          </div>
          {(['asset', 'town'] as const).map((kind) => {
            const rows = impacted.filter((p) => p.kind === kind);
            if (!rows.length) return null;
            return (
              <div key={kind} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Never interleaved: mixing them makes it easy to miss your own site. */}
                <div style={{ fontSize: 11.5, color: '#7d8ea8' }}>
                  {kind === 'asset' ? 'Your sites' : 'Towns — arrival at the town centre'}
                </div>
                {rows.map((p) => (
                  <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span
                      style={{
                        color: kind === 'asset' ? '#eaf1fb' : '#c9d7ee',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.name}
                    </span>
                    <span style={{ color: '#ffc36b', flexShrink: 0 }}>
                      {p.eta.status === 'inside'
                        ? 'now'
                        : formatEtaRange(p.eta)?.replace('in about ', '').replace('in ', '') ?? ''}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ fontSize: 11.5, color: '#7d8ea8' }}>
            Projected from the current track. Not an official NWS warning area.
          </div>
        </div>
      )}

      <div className="panel" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 4px' }}>
          <span className="lbl">Map tools</span>
          <button
            type="button"
            className="chip hov"
            onClick={() => {
              setMode('none');
              onClose();
            }}
            aria-label="Close map tools"
            style={{ width: 26, height: 26, padding: 0, borderRadius: 13, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        {TOOLS.map(([mode, label, title]) => (
          <button
            key={mode}
            type="button"
            className="chip hov"
            aria-pressed={state.mode === mode}
            title={title}
            onClick={() => setMode(mode)}
            style={{
              minHeight: 38,
              width: '100%',
              justifyContent: 'flex-start',
              background: state.mode === mode ? '#1b3566' : undefined,
              borderColor: state.mode === mode ? '#2f5aa8' : undefined,
            }}
          >
            {label}
          </button>
        ))}
        {state.mode === 'zone-polygon' && state.pending.length >= 3 && (
          <button type="button" className="chip hov" style={{ minHeight: 38, width: '100%' }} onClick={commitPolygon}>
            Finish zone
          </button>
        )}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            className="chip hov"
            style={{ minHeight: 38, flex: 1 }}
            onClick={undo}
            disabled={!hasContent && !state.pending.length}
          >
            Undo
          </button>
          <button type="button" className="chip hov" style={{ minHeight: 38, flex: 1 }} onClick={clear} disabled={!hasContent}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

/** Exported for the page that wires "Save as site"; keeps the API call out of here. */
export function zoneAsAssetDraft(zone: DrawnZone, name: string) {
  const [lon, lat] = zone.points[0]!;
  return {
    name,
    type: 'custom' as const,
    lat,
    lon,
    alertRadiusMiles: Math.min(100, Math.max(0, Math.round((zone.radiusMiles ?? 10) * 10) / 10)),
    description: 'Created from a drawn radius on the map',
  };
}
